import { Choice } from '@/types/choice'
import { Config } from '@/types/config'
import { Consultation } from '@/types/consultation'
import { Country } from '@/types/country'
import { AVAILABLE_LANGUAGES } from '@/types/language'
import { Module } from '@/types/module'
import { Reminder } from '@/types/reminder'
import { User } from '@/types/user'

export enum ACTION_TYPE {
  /*
    Initial config for the current partner.

    () => {
      config: Config
    }
  */
  GET_CONFIG = 'GET_CONFIG',

  /*
    First time user enters the app.

    (uniqueId?: string) => {
      user: {
        data: {
          uniqueId: string, // randomized if not existing user
          ... rest of user data if existing user
        },
        routeConditions: {
          group: "test|control", // randomized if not existing user
        }
      },
      module: Module, // initial module
    }
  */
  GET_INITIAL_MODULE = 'GET_INITIAL_MODULE',

  /*
    When user clicks back on a first step of a module.

    (uniqueId: string, currentModuleId: string, currentStepId: string) => {
      module: Module // previous module from db
    }
  */
  GET_PREVIOUS_MODULE = 'GET_PREVIOUS_MODULE',

  /*
    (uniqueId: string, moduleId: string) => {
      module: Module // next module
    }
  */
  GET_NEXT_MODULE = 'GET_NEXT_MODULE',

  /*
    (user: User) => {
      user: User // updated user
    }
  */
  UPDATE_USER = 'UPDATE_USER',

  /*
    (user: User) => {
      user: User // created user
    }
  */
  CREATE_USER = 'CREATE_USER',

  /*
    (choice: Choice) => {
      choice: Choice // choice set
    }
  */
  SET_CHOICE = 'SET_CHOICE',

  /*
    (choices: Choice[]) => {
      choices: Choice // reminder
    }
  */
  SET_REMINDER_WEEKLY = 'SET_REMINDER_WEEKLY',
  SET_REMINDER_PHYSICIAN = 'SET_REMINDER_PHYSICIAN',
  SET_REMINDER_PHARMACY = 'SET_REMINDER_PHARMACY',

  /*
    (userId: string) => {
      user: User // user
    }
  */
  GET_USER = 'GET_USER',

  /*
    (uniqueId: string) => {
      user: User // user
    }
  */
  SET_FLOW_COMPLETED = 'SET_FLOW_COMPLETED',

  /*
    () => {
      countries: Country[]
    }
  */
  GET_PHYSICIAN_COUNTRIES = 'GET_PHYSICIAN_COUNTRIES',

  /*
    (uniqueId: string, type: ROUTE_CONDITION_TYPE) => {
      user: User // User with updated
    }
  */
  GET_ROUTE_CONDITIONS = 'GET_ROUTE_CONDITIONS',

  /*
    () => {
      user: User // User with updated data
    }
  */
  SET_USER_INFO = 'SET_USER_INFO',

  /*
  (uniqueId: string) => Consultation
  */
  GET_CURRENT_USER_CONSULTATION = 'GET_CURRENT_USER_CONSULTATION',

  /*
    (uniqueId: string, question: string, attachments: string[]) => Consultation
  */
  CREATE_CONSULTATION = 'CREATE_CONSULTATION',

  /*
    (uniqueId: string, question: string, attachments: string[]) => Consultation
  */
  CREATE_FOLLOW_UP_CONSULTATION = 'CREATE_FOLLOW_UP_CONSULTATION',

  /*
    (uniqueId: string) => Consultation
  */
  CANCEL_CONSULTATION = 'CANCEL_CONSULTATION',

  /*
    (uniqueId: string, rate: number) => Consultation
  */
  RATE_CONSULTATION = 'RATE_CONSULTATION',

  /*
    (uniqueId: string, file: string) => {
      url: string
    }
  */
  UPLOAD_ATTACHMENT = 'UPLOAD_ATTACHMENT',

  /*
    (uniqueId: string) => {
      url: string
    }
  */
  GET_CONSULTATION_PDF = 'GET_CONSULTATION_PDF',

  /*
    (uniqueId: string) => {
      user: User // user
    }
  */
  RESET_TRACES = 'RESET_TRACES'
}

export enum ROUTE_CONDITION_TYPE {
  SANOFI_BMI = 'SANOFI_BMI'
}

export type OnLoadAction = ACTION_TYPE.SET_FLOW_COMPLETED

export type Action<T extends ACTION_TYPE> = T extends ACTION_TYPE.GET_CONFIG
  ? {
      type: T
      payload: {}
      response: {
        config: Config
      }
    }
  : T extends ACTION_TYPE.GET_INITIAL_MODULE
  ? {
      type: T
      payload: {
        uniqueId?: string
        userId?: string // partnerUserId
        language?: string
        country?: Country // Only used by Sanofi
      }
      response: {
        user: User
        module: Module
      }
    }
  : T extends ACTION_TYPE.GET_PREVIOUS_MODULE
  ? {
      type: T
      payload: {
        uniqueId: string
      }
      response: {
        module: Module
      }
    }
  : T extends ACTION_TYPE.GET_NEXT_MODULE
  ? {
      type: T
      payload: {
        uniqueId: string
        moduleId: string
      }
      response: {
        module: Module
      }
    }
  : T extends ACTION_TYPE.UPDATE_USER | ACTION_TYPE.CREATE_USER
  ? {
      type: T
      payload: {
        user: User['data']
      }
      response: User
    }
  : T extends ACTION_TYPE.SET_CHOICE
  ? {
      type: T
      payload: {
        uniqueId: string
        choice: Choice
      }
      response: {
        choice: Choice
      }
    }
  : T extends ACTION_TYPE.SET_REMINDER_PHARMACY | ACTION_TYPE.SET_REMINDER_PHYSICIAN | ACTION_TYPE.SET_REMINDER_WEEKLY
  ? {
      type: T
      payload: {
        uniqueId: string
        reminder?: string
        timezoneOffset: number
      }
      response: {
        reminder: Reminder
      }
    }
  : T extends ACTION_TYPE.GET_USER
  ? {
      type: T
      payload: {
        userId: string
      }
      response: User
    }
  : T extends ACTION_TYPE.SET_FLOW_COMPLETED
  ? {
      type: T
      payload: {
        uniqueId: string
      }
      response: User
    }
  : T extends ACTION_TYPE.GET_PHYSICIAN_COUNTRIES
  ? {
      type: T
      payload: Record<string, never>
      response: Country[]
    }
  : T extends ACTION_TYPE.GET_ROUTE_CONDITIONS
  ? {
      type: T
      payload: {
        uniqueId: string
        type: ROUTE_CONDITION_TYPE
      }
      response: User
    }
  : T extends ACTION_TYPE.SET_USER_INFO
  ? {
      type: T
      payload: Partial<User['data']>
      response: User
    }
  : T extends ACTION_TYPE.GET_CURRENT_USER_CONSULTATION
  ? {
      type: T
      payload: {
        uniqueId: string
      }
      response: Consultation | null // Last consultation or null if user has no consultations yet
    }
  : T extends ACTION_TYPE.CREATE_CONSULTATION
  ? {
      type: T
      payload: {
        uniqueId: string
        question: string
        attachments: string[]
      }
      response: Consultation
    }
  : T extends ACTION_TYPE.CREATE_FOLLOW_UP_CONSULTATION
  ? {
      type: T
      payload: {
        uniqueId: string
        question: string
        attachments: string[]
      }
      response: Consultation
    }
  : T extends ACTION_TYPE.CANCEL_CONSULTATION
  ? {
      type: T
      payload: {
        uniqueId: string
      }
      response: Consultation
    }
  : T extends ACTION_TYPE.RATE_CONSULTATION
  ? {
      type: T
      payload: {
        uniqueId: string
        rating: number
      }
      response: Consultation
    }
  : T extends ACTION_TYPE.UPLOAD_ATTACHMENT
  ? {
      type: T
      payload: {
        uniqueId: string
        file: string // base64
      }
      response: {
        url: string
      }
    }
  : T extends ACTION_TYPE.GET_CONSULTATION_PDF
  ? {
      type: T
      payload: {
        uniqueId: string
      }
      response: {
        url: string
      }
    }
  : T extends ACTION_TYPE.RESET_TRACES
  ? {
      type: T
      payload: {
        uniqueId: string
      }
      response: User
    }
  : never
